// src/components/files/FileGrid.tsx
import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useFileManager } from '../../contexts/FileManagerContext';
import FileItem from './FileItem';
import FilePreview from './FilePreview';
import { FileDocument } from '../../types/files';

const FileGrid: React.FC = () => {
  const { state, navigateToFolder, selectItem, isSelected, clearSelection } = useFileManager();
  const { contents, sortBy, sortOrder } = state;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileDocument | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  if (!contents) return null;

  // Sort items
  const sortedFolders = [...contents.folders].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'date':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      default:
        comparison = a.name.localeCompare(b.name);
    }
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const sortedDocuments = [...contents.documents].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'date':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      case 'size':
        comparison = a.file_size - b.file_size;
        break;
      default:
        comparison = a.title.localeCompare(b.title);
    }
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  const handleFolderDoubleClick = (folderId: number) => {
    navigateToFolder(folderId);
  };

  const handleDocumentDoubleClick = (doc: FileDocument) => {
    const index = sortedDocuments.findIndex((d) => d.id === doc.id);
    setPreviewFile(doc);
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const handlePreviewNavigate = (index: number) => {
    if (index >= 0 && index < sortedDocuments.length) {
      setPreviewFile(sortedDocuments[index]);
      setPreviewIndex(index);
    }
  };

  const handleItemClick = (
    e: React.MouseEvent,
    id: number,
    type: 'folder' | 'document'
  ) => {
    selectItem({ id, type }, e.ctrlKey || e.metaKey, e.shiftKey);
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Only clear if clicking directly on the container
    if (e.target === e.currentTarget) {
      clearSelection();
    }
  };

  return (
    <Box onClick={handleBackgroundClick} sx={{ minHeight: '100%' }}>
      <Grid container spacing={2}>
        {/* Folders first */}
        {sortedFolders.map((folder) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={`folder-${folder.id}`}>
            <FileItem
              type="folder"
              id={folder.id}
              name={folder.name}
              color={folder.color}
              icon={folder.icon}
              isFavorite={folder.is_favorite}
              createdAt={folder.created_at}
              isSelected={isSelected(folder.id, 'folder')}
              onClick={(e) => handleItemClick(e, folder.id, 'folder')}
              onDoubleClick={() => handleFolderDoubleClick(folder.id)}
              viewMode="grid"
            />
          </Grid>
        ))}

        {/* Then documents */}
        {sortedDocuments.map((doc) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={`doc-${doc.id}`}>
            <FileItem
              type="document"
              id={doc.id}
              name={doc.title}
              mimeType={doc.file_type}
              fileSize={doc.file_size}
              fileUrl={doc.file_url}
              createdAt={doc.created_at}
              isFavorite={doc.is_favorite}
              isSelected={isSelected(doc.id, 'document')}
              onClick={(e) => handleItemClick(e, doc.id, 'document')}
              onDoubleClick={() => handleDocumentDoubleClick(doc)}
              viewMode="grid"
            />
          </Grid>
        ))}
      </Grid>

      {/* File Preview Dialog */}
      <FilePreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        file={previewFile}
        files={sortedDocuments}
        currentIndex={previewIndex}
        onNavigate={handlePreviewNavigate}
      />
    </Box>
  );
};

export default FileGrid;
